import React from "react";
import { Button } from "reactstrap";

import { isInteractiveTarget, resolveItemImage } from "../../views/saleItemPresentation";

/**
 * One product/variant search result on a store operation page (Sales manual
 * search, Exchanges replacement search).
 *
 * Presentation only. The page passes its own `onAdd` - the exact handler its Add
 * button already uses - so double click can never bypass stock, serialized or
 * duplicate rules. Double click only fires for rows the page marked addable.
 */
const SaleItemResultRow = ({
  item,
  title,
  chips = [],
  sku,
  price,
  stockLabel,
  stockTone = "neutral",
  stockHint,
  actionLabel,
  isAddable,
  actionDisabled = false,
  actionTitle,
  actionAriaLabel,
  doubleClickHint,
  onAdd,
}) => {
  const image = resolveItemImage(item);
  const canActivate = Boolean(isAddable && !actionDisabled);

  const handleDoubleClick = (event) => {
    // The button already handles its own clicks; never add twice.
    if (!canActivate || isInteractiveTarget(event.target)) {
      return;
    }
    onAdd(item);
  };

  const rowTitle = [canActivate ? doubleClickHint : null, sku ? `SKU ${sku}` : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className={`sir-row${canActivate ? " is-addable" : " is-disabled"}`}
      onDoubleClick={handleDoubleClick}
      title={rowTitle || undefined}
    >
      <div className="sir-row__media">
        {image ? (
          <img src={image} alt={item?.imageAltText || ""} loading="lazy" />
        ) : (
          <span className="sir-row__media-empty" aria-hidden="true" />
        )}
      </div>

      <div className="sir-row__main">
        <span className="sir-row__title">{title}</span>
        {chips.length > 0 && (
          <div className="sir-row__chips">
            {chips.map((chip) => (
              <span className="sir-chip" key={chip}>
                {chip}
              </span>
            ))}
          </div>
        )}
        {sku && <span className="sir-row__sku">SKU {sku}</span>}
      </div>

      <div className="sir-row__commerce">
        <span className="sir-row__price">{price}</span>
        <span className={`sir-row__stock sir-row__stock--${stockTone}`}>
          <span className="sir-stock-dot" aria-hidden="true" />
          {stockLabel}
        </span>
        {stockHint && <span className="sir-row__stock-hint">{stockHint}</span>}
      </div>

      <Button
        color={canActivate ? "primary" : "secondary"}
        className="sir-row__action"
        disabled={!canActivate}
        onClick={() => onAdd(item)}
        title={actionTitle || undefined}
        aria-label={actionAriaLabel || undefined}
      >
        {canActivate && <i className="tim-icons icon-simple-add" aria-hidden="true" />}
        {actionLabel}
      </Button>
    </div>
  );
};

export default SaleItemResultRow;
